import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getAccountKeyForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { AccountStatus, App, BootState, getAccounts } from 'owa-account-source-list-store';
import { isConsumer } from 'owa-session-store';
import {
    lazyFetchMicrosoft365UpgradeEligibility,
    lazyGetMicrosoft365UpgradeTier,
} from 'owa-header-microsoft365-upsell';
import {
    lazyGetOutlookHeaderM365UpsellButtonIrisContent,
    lazyLoadOutlookHeaderM365UpsellButtonIrisContent,
    type OutlookHeaderM365UpsellButtonContent,
} from 'owa-header-microsoft365-upsell-iris';
import { shouldShowUpsellOption } from 'owa-storage-common/lib/utils/shouldShowUpsellOption';
import { logUsage } from 'owa-analytics';

/**
 * The upsell shown by the persistent Microsoft 365 header button, or `hidden` when it should not
 * show at all. The upsell is only available when every signed-in account is a consumer account;
 * within those sessions, two independent upsells share the one button, each behind its own flight:
 *  - `buy`: "Buy Microsoft 365" (header-persistentBuyM365Upsell) for consumers with no subscription.
 *  - `upgradeBasic`: "Upgrade your plan" for Microsoft 365 Basic subscribers.
 *  - `upgradePersonalOrFamily`: the same button for Personal or Family subscribers.
 * Both upgrade modes are gated by header-persistentUpgradeM365Upsell but route to separate DIME
 * configurations.
 */
export type Microsoft365UpsellMode = 'hidden' | 'buy' | 'upgradeBasic' | 'upgradePersonalOrFamily';

type Microsoft365UpgradeTier = 'basic' | 'personalOrFamily';

type GetMicrosoft365UpgradeTier = (mailboxInfo: MailboxInfo) => Microsoft365UpgradeTier | undefined;

interface Microsoft365UpsellResource {
    getUpgradeTier?: GetMicrosoft365UpgradeTier;
    irisContent?: OutlookHeaderM365UpsellButtonContent;
    irisVisibilityPromise?: Promise<boolean>;
    visibilityPromise?: Promise<boolean>;
    mode: Microsoft365UpsellMode;
}

const upsellResources = new Map<
    ReturnType<typeof getAccountKeyForMailboxInfo>,
    Microsoft365UpsellResource
>();

export function isMicrosoft365UpgradeMode(mode: Microsoft365UpsellMode): boolean {
    return mode === 'upgradeBasic' || mode === 'upgradePersonalOrFamily';
}

/**
 * Single source of truth for the Microsoft 365 header upsell. Visibility, label, tooltip, and the
 * launched Dime experience all derive from this so they can never disagree (e.g. showing "Buy" copy
 * while routing an "Upgrade" click).
 *
 * The upgrade modes take precedence over `buy`. Their subscription selector is cached after the
 * header's asynchronous visibility check initializes each account, keeping the premium
 * subscription chain out of AppBoot while label, tooltip, and click routing remain synchronous.
 */
export function getMicrosoft365UpsellMode(mailboxInfo: MailboxInfo): Microsoft365UpsellMode {
    return getUpsellResource(mailboxInfo)?.mode ?? 'hidden';
}

export function getMicrosoft365UpsellIrisContent(
    mailboxInfo: MailboxInfo
): OutlookHeaderM365UpsellButtonContent | undefined {
    return getUpsellResource(mailboxInfo)?.irisContent;
}

function resolveMicrosoft365UpsellMode(
    mailboxInfo: MailboxInfo,
    resource: Microsoft365UpsellResource,
    isBuyEnabled: boolean,
    isUpgradeEnabled: boolean
): Microsoft365UpsellMode {
    if (isUpgradeEnabled) {
        const upgradeTier = resource.getUpgradeTier?.(mailboxInfo);
        if (upgradeTier === 'basic') {
            return 'upgradeBasic';
        }
        if (upgradeTier === 'personalOrFamily') {
            return 'upgradePersonalOrFamily';
        }
    }

    if (isBuyEnabled && shouldShowUpsellOption(mailboxInfo, false /* isMSQ */)) {
        return 'buy';
    }

    return 'hidden';
}

export function isMicrosoft365UpsellVisible(mailboxInfo: MailboxInfo): boolean | Promise<boolean> {
    hideCachedUpsell(mailboxInfo);

    // Only consumer accounts are ever offered the upsell; short-circuit before checking any flight
    // or loading the eligibility chunk for non-consumer accounts.
    if (
        !isConsumer(undefined /* smtpAddress */, mailboxInfo) ||
        isUnifiedInboxMailboxInfo(mailboxInfo)
    ) {
        return false;
    }

    const isUpgradeEnabled = isGlobalFeatureEnabled('header-persistentUpgradeM365Upsell');
    const isBuyEnabled = isGlobalFeatureEnabled('header-persistentBuyM365Upsell');
    if (!isUpgradeEnabled && !isBuyEnabled) {
        return false;
    }

    if (!areAllLoggedInMailboxesConsumers()) {
        return false;
    }

    const resource = getOrCreateUpsellResource(mailboxInfo);

    if (isUpgradeEnabled) {
        if (resource.getUpgradeTier) {
            return resolveMicrosoft365UpsellVisibility(
                mailboxInfo,
                resource,
                isBuyEnabled,
                isUpgradeEnabled
            );
        }

        if (!resource.visibilityPromise) {
            resource.visibilityPromise = loadUpgradeResource(mailboxInfo, resource);
        }

        return resource.visibilityPromise;
    }

    return resolveMicrosoft365UpsellVisibility(
        mailboxInfo,
        resource,
        isBuyEnabled,
        isUpgradeEnabled
    );
}

function resolveMicrosoft365UpsellVisibility(
    mailboxInfo: MailboxInfo,
    resource: Microsoft365UpsellResource,
    isBuyEnabled: boolean,
    isUpgradeEnabled: boolean
): boolean | Promise<boolean> {
    resource.mode = resolveMicrosoft365UpsellMode(
        mailboxInfo,
        resource,
        isBuyEnabled,
        isUpgradeEnabled
    );
    if (resource.mode === 'hidden') {
        return false;
    }
    if (!isMicrosoft365UpsellIrisEnabled()) {
        resource.irisContent = undefined;
        resource.irisVisibilityPromise = undefined;
        return true;
    }

    if (!resource.irisVisibilityPromise) {
        resource.irisVisibilityPromise = loadMicrosoft365UpsellIrisContent(mailboxInfo, resource);
    }
    return resource.irisVisibilityPromise;
}

function isMicrosoft365UpsellIrisEnabled(): boolean {
    return (
        isGlobalFeatureEnabled('growth-irisforpersistentheaderupsell') ||
        isGlobalFeatureEnabled('growth-irisforpersistentheaderupsell-test')
    );
}

function loadMicrosoft365UpsellIrisContent(
    mailboxInfo: MailboxInfo,
    resource: Microsoft365UpsellResource
): Promise<boolean> {
    return (async () => {
        const hasIrisContent =
            await lazyLoadOutlookHeaderM365UpsellButtonIrisContent.importAndExecute(mailboxInfo);
        if (hasIrisContent && isMicrosoft365UpsellIrisEnabled()) {
            const getIrisContent = await lazyGetOutlookHeaderM365UpsellButtonIrisContent.import();
            resource.irisContent = getIrisContent(mailboxInfo);
        }
        return true;
    })().catch(() => handleMicrosoft365UpsellIrisLoadFailure(mailboxInfo));
}

function handleMicrosoft365UpsellIrisLoadFailure(mailboxInfo: MailboxInfo): true {
    logUsage('HeaderMicrosoft365UpsellIrisLoadFailed', mailboxInfo);
    return true;
}

function areAllLoggedInMailboxesConsumers(): boolean {
    const accounts = getAccounts(AccountStatus.Any, App.All);
    return (
        accounts.length > 0 &&
        accounts.every(
            account =>
                account.bootState === BootState.StartupComplete &&
                isConsumer(undefined /* smtpAddress */, account.mailboxInfo)
        )
    );
}

function getUpsellResource(mailboxInfo: MailboxInfo): Microsoft365UpsellResource | undefined {
    return upsellResources.get(getAccountKeyForMailboxInfo(mailboxInfo));
}

function hideCachedUpsell(mailboxInfo: MailboxInfo): void {
    if (upsellResources.size === 0) {
        return;
    }

    const resource = getUpsellResource(mailboxInfo);
    if (resource) {
        resource.mode = 'hidden';
    }
}

function getOrCreateUpsellResource(mailboxInfo: MailboxInfo): Microsoft365UpsellResource {
    const mailboxKey = getAccountKeyForMailboxInfo(mailboxInfo);
    let resource = upsellResources.get(mailboxKey);
    if (!resource) {
        resource = { mode: 'hidden' };
        upsellResources.set(mailboxKey, resource);
    }
    return resource;
}

function loadUpgradeResource(
    mailboxInfo: MailboxInfo,
    resource: Microsoft365UpsellResource
): Promise<boolean> {
    const visibilityPromise = (async () => {
        const fetchEligibility = await lazyFetchMicrosoft365UpgradeEligibility.import();
        await fetchEligibility(mailboxInfo);

        resource.getUpgradeTier = await lazyGetMicrosoft365UpgradeTier.import();
        return resolveMicrosoft365UpsellVisibility(
            mailboxInfo,
            resource,
            isGlobalFeatureEnabled('header-persistentBuyM365Upsell'),
            true /* isUpgradeEnabled */
        );
    })();

    visibilityPromise.catch(() => {
        if (resource.visibilityPromise === visibilityPromise) {
            upsellResources.delete(getAccountKeyForMailboxInfo(mailboxInfo));
        }
    });

    return visibilityPromise;
}

export function resetMicrosoft365UpgradeResourcesForTest(): void {
    upsellResources.clear();
}
